import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { Button } from 'blaze-ui/Button'
import { useState, useRef, useCallback } from 'react'
import cx from 'classnames'
import { PlanIcon } from 'components/Settings/TieredBilling/components/PlanIcon/PlanIcon'
import { pluralize } from 'utils'
import Styles from './AllPlans.module.scss'

// Icons
import {
  Brand,
  Calendar2,
  Hash2,
  Knowledge,
  LightningFilled,
  MessageText2,
  Approval,
  ArrowSkip,
  ArrowLeft,
} from 'blaze-ui/icons'
import { BlazeAI } from 'almanac-ui/icons'
import FileSearch1 from 'blaze-ui/icons/20/FileSearch1'
import UserProfile1 from 'blaze-ui/icons/20/UserProfile1'
import PhoneCall01 from 'blaze-ui/icons/16/PhoneCall01'
import FileCheck02 from 'blaze-ui/icons/16/FileCheck02'
import Campaign from 'blaze-ui/icons/16/Campaign'
import { ArrowRightSquareContained } from 'blaze-ui/icons/20/ArrowRightSquareContained'
import Bezier01 from 'blaze-ui/icons/16/Bezier01'
import Iphone02 from 'blaze-ui/icons/16/Iphone02'
import { SwipeableTabs } from 'components/SwipeableTabs/SwipeableTabs'
import { Item } from '@react-stately/collections'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { PlanFeatureRow } from './PlanFeatureRow'
import { BillingFrequency } from 'stores/PaywallStore'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { SegmentedControl } from 'blaze-ui/SegmentedControl/SegmentedControl'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { PlanColumn } from './PlanColumn'
import { useCurrentPlan } from 'hooks/useCurrentPlan'

export const AllPlans = observer(() => {
  const { tierUsageStore, paywallStore } = useRootStore()
  const { paymentPlans } = tierUsageStore
  const { isMobile } = useResponsiveDesign()
  const { data: currentPlan } = useCurrentPlan()

  // Map between tab keys and plan keys
  const tabKeyToPlanKey: Record<string, PaymentPlanKey> = {
    creator: PaymentPlanKey.BlazeCreator,
    pro: PaymentPlanKey.BlazePro,
    team: PaymentPlanKey.BlazeTeam,
    agency: PaymentPlanKey.BlazeAgency,
  }

  const planKeyToTabKey: Record<string, string> = {
    [PaymentPlanKey.BlazeCreator]: 'creator',
    [PaymentPlanKey.BlazePro]: 'pro',
    [PaymentPlanKey.BlazeTeam]: 'team',
    [PaymentPlanKey.BlazeAgency]: 'agency',
  }

  // Use the initial tab based on selectedPlanKey
  const [currentTab, setCurrentTab] = useState<string>(planKeyToTabKey[paywallStore.selectedPlanKey])
  const [isScrolled, setIsScrolled] = useState(false)

  const tabs = useRef<Record<string, HTMLDivElement>>({})

  const addRefFunction = useCallback(
    (key: string) => (element: HTMLDivElement) => {
      if (element) {
        tabs.current[key] = element
      }
    },
    [],
  )

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setIsScrolled(e.currentTarget.scrollTop > 0)
  }, [])

  const handleSelectPlan = () => {
    paywallStore.goToCheckout()
  }

  const trialDaysLeft = currentPlan?.trial_days_left ?? -1

  const renderPlanContent = (planKey: PaymentPlanKey) => {
    const plan = paymentPlans.find((p) => p.key === planKey)
    if (!plan) return null

    if (!isMobile) {
      return (
        <PlanColumn
          plan={plan}
          discount={
            plan.key === PaymentPlanKey.BlazePro &&
            paywallStore.selectedBillingFrequency === BillingFrequency.ANNUAL &&
            trialDaysLeft > 0
          }
          onPlanSelect={(plan) => {
            paywallStore.selectPlan(plan)
            paywallStore.goToCheckout()
          }}
          frequency={paywallStore.selectedBillingFrequency}
        />
      )
    }

    let monthlyPrice =
      paywallStore.selectedBillingFrequency === BillingFrequency.ANNUAL
        ? Math.round(plan.price.year.unit_amount / 12)
        : plan.price.month.unit_amount

    if (trialDaysLeft > 0 && planKey === PaymentPlanKey.BlazePro) {
      monthlyPrice = Math.round(monthlyPrice * 0.92)
    }

    let planColor
    switch (planKey) {
      case PaymentPlanKey.BlazeCreator:
        planColor = '#4caf50'
        break
      case PaymentPlanKey.BlazePro:
        planColor = '#6c5ce7'
        break
      case PaymentPlanKey.BlazeTeam:
        planColor = '#ff4f97'
        break
      case PaymentPlanKey.BlazeAgency:
        planColor = '#e74c3c'
        break
      default:
        planColor = '#4caf50'
    }

    let featureHeaderText
    switch (planKey) {
      case PaymentPlanKey.BlazePro:
        featureHeaderText = 'Everything from Creator and:'
        break
      case PaymentPlanKey.BlazeTeam:
        featureHeaderText = 'Everything from Pro and:'
        break
      case PaymentPlanKey.BlazeAgency:
        featureHeaderText = 'Everything from Team and:'
        break
      default:
        featureHeaderText = 'Includes:'
    }

    return (
      <div className={Styles.planContent}>
        <div
          className={cx(Styles.planHeader, {
            [Styles.creatorHeader]: planKey === PaymentPlanKey.BlazeCreator,
            [Styles.proHeader]: planKey === PaymentPlanKey.BlazePro,
            [Styles.teamHeader]: planKey === PaymentPlanKey.BlazeTeam,
            [Styles.agencyHeader]: planKey === PaymentPlanKey.BlazeAgency,
          })}
        >
          <ResponsiveDesign.Mobile>
            <div className={Styles.planIconTitle}>
              <PlanIcon size={30} plan={planKey} />
              <Heading level={1} color="var(--light-100)">
                {plan.name}
              </Heading>
            </div>
            <div className={Styles.pricingInfo}>
              <Heading level={3} color="var(--light-100)">
                ${monthlyPrice}
              </Heading>
              <div className={Styles.billingPeriod}>
                <Text variant="metadata" color="var(--light-60)">
                  per month
                </Text>
                <Text variant="metadata" color="var(--light-60)">
                  {paywallStore.selectedBillingFrequency === BillingFrequency.ANNUAL ? ` billed annually` : ''}
                </Text>
              </div>
            </div>
          </ResponsiveDesign.Mobile>
          <ResponsiveDesign.Desktop>
            <div className={Styles.planPricing}>
              <div className={Styles.planIconTitle}>
                <PlanIcon size={20} plan={planKey} />
                <Heading level={4} color="var(--light-100)">
                  {plan.name}
                </Heading>
              </div>
            </div>
          </ResponsiveDesign.Desktop>
        </div>

        <ResponsiveDesign.Mobile>
          <div className={Styles.frequencyToggle}>
            <div
              className={cx(Styles.frequencyOption, Styles.annual, {
                [Styles.active]: paywallStore.selectedBillingFrequency === BillingFrequency.ANNUAL,
                [Styles.creatorToggle]:
                  planKey === PaymentPlanKey.BlazeCreator &&
                  paywallStore.selectedBillingFrequency === BillingFrequency.ANNUAL,
                [Styles.proToggle]:
                  planKey === PaymentPlanKey.BlazePro &&
                  paywallStore.selectedBillingFrequency === BillingFrequency.ANNUAL,
                [Styles.teamToggle]:
                  planKey === PaymentPlanKey.BlazeTeam &&
                  paywallStore.selectedBillingFrequency === BillingFrequency.ANNUAL,
                [Styles.agencyToggle]:
                  planKey === PaymentPlanKey.BlazeAgency &&
                  paywallStore.selectedBillingFrequency === BillingFrequency.ANNUAL,
              })}
              onClick={() => paywallStore.selectBillingFrequency(BillingFrequency.ANNUAL)}
            >
              <span className={Styles.frequencyLabel}>Annual</span>{' '}
              {planKey === PaymentPlanKey.BlazePro ? '33%' : '25%'} OFF
            </div>
            <div
              className={cx(Styles.frequencyOption, Styles.monthly, {
                [Styles.active]: paywallStore.selectedBillingFrequency === BillingFrequency.MONTHLY,
                [Styles.creatorToggle]:
                  planKey === PaymentPlanKey.BlazeCreator && paywallStore.selectedBillingFrequency === 'monthly',
                [Styles.proToggle]:
                  planKey === PaymentPlanKey.BlazePro && paywallStore.selectedBillingFrequency === 'monthly',
                [Styles.teamToggle]:
                  planKey === PaymentPlanKey.BlazeTeam && paywallStore.selectedBillingFrequency === 'monthly',
                [Styles.agencyToggle]:
                  planKey === PaymentPlanKey.BlazeAgency && paywallStore.selectedBillingFrequency === 'monthly',
              })}
              onClick={() => paywallStore.selectBillingFrequency(BillingFrequency.MONTHLY)}
            >
              <span className={Styles.frequencyLabel}>Monthly</span>
            </div>
          </div>
        </ResponsiveDesign.Mobile>

        <div className={Styles.featuresSection}>
          <Heading level={5}>{featureHeaderText}</Heading>

          <PlanFeatureRow
            limit={plan.base_price_meta.seats_limit}
            icon={<UserProfile1 size={20} color={planColor} />}
            feature={pluralize('Seat', parseInt(`${plan.base_price_meta.seats_limit}`) || 0)}
          />
          <PlanFeatureRow
            limit={plan.base_price_meta.brand_kits_limit}
            icon={<Brand size={20} color={planColor} />}
            feature={'Brand Kits'}
          />
          <PlanFeatureRow
            limit={plan.base_price_meta.integrations_limit}
            icon={<Iphone02 size={20} color={planColor} />}
            feature={'Accounts per platform'}
          />
          <PlanFeatureRow
            limit={plan.base_price_meta.published_posts_limit}
            icon={<LightningFilled size={20} color={planColor} />}
            feature={'Published posts per platform'}
          />
          <PlanFeatureRow
            limit={
              plan.base_price_meta.ai_words_limit === 'unlimited'
                ? 'Unlimited'
                : `${parseInt(`${plan.base_price_meta.ai_words_limit}`) / 1000}K`
            }
            icon={<BlazeAI size={20} color={planColor} />}
            feature={'AI Words'}
          />
          <PlanFeatureRow
            limit={plan.base_price_meta.plagiarism_checks_limit}
            icon={<FileSearch1 size={20} color={planColor} />}
            feature={'Plagiarism Scans'}
          />
          <PlanFeatureRow
            limit={plan.base_price_meta.source_materials_limit}
            icon={<Knowledge size={20} color={planColor} />}
            feature={'Source Materials'}
          />

          {/* Extra features based on plan */}
          {planKey === PaymentPlanKey.BlazeCreator && (
            <>
              <PlanFeatureRow icon={<PhoneCall01 size={20} color={planColor} />} feature={'Unlimited docs & designs'} />
              <PlanFeatureRow icon={<UserProfile1 size={20} color={planColor} />} feature={'Unlimited guest access'} />
              <PlanFeatureRow icon={<Calendar2 size={20} color={planColor} />} feature={'Social media calendar'} />
              <PlanFeatureRow icon={<Campaign size={20} color={planColor} />} feature={'Multi-channel campaign'} />
              <PlanFeatureRow icon={<FileCheck02 size={20} color={planColor} />} feature={'Unlimited SEO checks'} />
            </>
          )}

          {planKey === PaymentPlanKey.BlazePro && (
            <>
              <PlanFeatureRow icon={<Hash2 size={20} color={planColor} />} feature={'Link & hashtag banks'} />
              <PlanFeatureRow
                icon={<ArrowRightSquareContained size={20} color={planColor} />}
                feature={'Multiple post wizards'}
              />
              <PlanFeatureRow icon={<PhoneCall01 size={20} color={planColor} />} feature={'Priority support'} />
              <PlanFeatureRow icon={<ArrowSkip size={20} color={planColor} />} feature={'Priority AI processing'} />
            </>
          )}

          {planKey === PaymentPlanKey.BlazeTeam && (
            <>
              <PlanFeatureRow
                icon={<MessageText2 size={20} color={planColor} />}
                feature={'Real time collaboration, Comments & Tasks'}
              />
              <PlanFeatureRow icon={<Approval size={20} color={planColor} />} feature={'Content approval tools'} />
            </>
          )}

          {planKey === PaymentPlanKey.BlazeAgency && (
            <>
              <PlanFeatureRow
                icon={<PhoneCall01 size={20} color={planColor} />}
                feature={'Dedicated onboarding & workspace setup'}
              />
              <PlanFeatureRow
                icon={<Bezier01 size={20} color={planColor} />}
                feature={'Multi-workspace volume discounts'}
              />
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={Styles.container}>
      <ResponsiveDesign.Mobile>
        <div className={cx(Styles.header, { [Styles.scrolled]: isScrolled })}>
          <Button variant="ghost" onPress={() => paywallStore.goToPlanSelect()} color="var(--dark-80)">
            Back
          </Button>
          <Text color="var(--dark-80)">Blaze Plans</Text>
          <div className={Styles.spacer}></div>
        </div>
        <SwipeableTabs
          aria-label="Pricing Plans"
          selectedKey={currentTab}
          onSelectionChange={(key) => {
            const newTab = key as string
            setCurrentTab(newTab)
            paywallStore.selectPlan(tabKeyToPlanKey[newTab])
            setIsScrolled(tabs.current[newTab]?.clientHeight < window.innerHeight)
          }}
        >
          <Item key="creator" title="Creator">
            <div className={Styles.tabContent} onScroll={handleScroll} ref={addRefFunction('creator')}>
              {renderPlanContent(PaymentPlanKey.BlazeCreator)}
            </div>
          </Item>
          <Item key="pro" title="Pro">
            <div className={Styles.tabContent} onScroll={handleScroll} ref={addRefFunction('pro')}>
              {renderPlanContent(PaymentPlanKey.BlazePro)}
            </div>
          </Item>
          <Item key="team" title="Team">
            <div className={Styles.tabContent} onScroll={handleScroll} ref={addRefFunction('team')}>
              {renderPlanContent(PaymentPlanKey.BlazeTeam)}
            </div>
          </Item>
          <Item key="agency" title="Agency">
            <div className={Styles.tabContent} onScroll={handleScroll} ref={addRefFunction('agency')}>
              {renderPlanContent(PaymentPlanKey.BlazeAgency)}
            </div>
          </Item>
        </SwipeableTabs>
      </ResponsiveDesign.Mobile>
      <ResponsiveDesign.Desktop>
        <div className={Styles.header}>
          <div className={Styles.headerLeft}>
            <IconButton variant="tertiary" onPress={() => paywallStore.goToPlanSelect()} icon={ArrowLeft} size="xs" />
            <Heading level={3}>Select a plan</Heading>
          </div>
          <SegmentedControl
            variant="secondary"
            rounded
            activeItem={paywallStore.selectedBillingFrequency}
            onItemClick={(frequency) => {
              paywallStore.selectBillingFrequency(frequency as BillingFrequency)
            }}
          >
            <Item key={BillingFrequency.MONTHLY} textValue="Monthly">
              Billed Monthly
            </Item>
            <Item key={BillingFrequency.ANNUAL} textValue="Annual">
              Yearly{' '}
              <span
                className={cx(Styles.frequencyDiscount, {
                  [Styles.active]: paywallStore.selectedBillingFrequency === BillingFrequency.ANNUAL,
                })}
              >
                Save 25%
              </span>
            </Item>
          </SegmentedControl>
        </div>
        <div className={Styles.plans}>
          {Object.keys(tabKeyToPlanKey).map((key) => {
            const planKey = tabKeyToPlanKey[key]
            return (
              <div
                key={key}
                className={cx(Styles.planTab, {
                  [Styles.active]: currentTab === key,
                })}
                onClick={() => {
                  setCurrentTab(key)
                  paywallStore.selectPlan(planKey)
                  setIsScrolled(tabs.current[key]?.clientHeight < window.innerHeight)
                }}
              >
                {renderPlanContent(planKey)}
              </div>
            )
          })}
        </div>
      </ResponsiveDesign.Desktop>
      <ResponsiveDesign.Mobile>
        <div className={Styles.footer}>
          <Button variant="primary" fullWidth onPress={handleSelectPlan} size="xl">
            Select Plan
          </Button>
        </div>
      </ResponsiveDesign.Mobile>
    </div>
  )
})
